import type { CriterionType, CustomerValueCriterion, CustomerValueScoreTemplate } from "../types";

export interface CriterionScoreInput {
  score: number;
  weight: number;
  criterionType: CriterionType;
}

export const DEFAULT_SCORE_TEMPLATES: Omit<CustomerValueScoreTemplate, "id" | "modelId" | "workspaceId" | "createdAt" | "updatedAt">[] = [
  {
    segmentName: "Höchste Wertspanne",
    minScore: 80,
    maxScore: 100,
    recommendation: "Anhand der finalen Kriterien und Gewichtungen prüfen."
  },
  {
    segmentName: "Starke Wertspanne",
    minScore: 70,
    maxScore: 79,
    recommendation: "Anhand der finalen Kriterien und Gewichtungen prüfen."
  },
  {
    segmentName: "Entwicklungsspanne",
    minScore: 60,
    maxScore: 69,
    recommendation: "Anhand der finalen Kriterien und Gewichtungen prüfen."
  },
  {
    segmentName: "Prüfspanne",
    minScore: 45,
    maxScore: 59,
    recommendation: "Anhand der finalen Kriterien und Gewichtungen prüfen."
  },
  {
    segmentName: "Niedrige Kundenwertspanne",
    minScore: 0,
    maxScore: 44,
    recommendation: "Anhand der finalen Kriterien und Gewichtungen prüfen."
  }
];

export const DEFAULT_SCORE_TEMPLATES_EN: Omit<CustomerValueScoreTemplate, "id" | "modelId" | "workspaceId" | "createdAt" | "updatedAt">[] = [
  {
    segmentName: "Highest Value Range",
    minScore: 80,
    maxScore: 100,
    recommendation: "Review based on the finalized criteria and weightings."
  },
  {
    segmentName: "Strong Value Range",
    minScore: 70,
    maxScore: 79,
    recommendation: "Review based on the finalized criteria and weightings."
  },
  {
    segmentName: "Development Range",
    minScore: 60,
    maxScore: 69,
    recommendation: "Review based on the finalized criteria and weightings."
  },
  {
    segmentName: "Review Range",
    minScore: 45,
    maxScore: 59,
    recommendation: "Review based on the finalized criteria and weightings."
  },
  {
    segmentName: "Low Customer Value Range",
    minScore: 0,
    maxScore: 44,
    recommendation: "Review based on the finalized criteria and weightings."
  }
];

export function getDefaultScoreTemplates(language: "de" | "en" = "de") {
  return language === "en" ? DEFAULT_SCORE_TEMPLATES_EN : DEFAULT_SCORE_TEMPLATES;
}

export function calculateWeightedCriterionScore(input: CriterionScoreInput): number {
  const normalizedScore = Math.min(5, Math.max(1, input.score));
  if (input.criterionType === "negative") {
    return (6 - normalizedScore) * input.weight;
  }

  return normalizedScore * input.weight;
}

export function calculateCustomerValueScore(criteriaScores: CriterionScoreInput[]): number {
  const activeScores = criteriaScores.filter((criterion) => criterion.weight > 0);
  const maxScore = activeScores.reduce((sum, criterion) => sum + 5 * criterion.weight, 0);
  if (maxScore === 0) return 0;

  const weightedScore = activeScores.reduce(
    (sum, criterion) => sum + calculateWeightedCriterionScore(criterion),
    0
  );

  return Math.round((weightedScore / maxScore) * 100);
}

export function assignCustomerValueSegment(score: number): string {
  const template = DEFAULT_SCORE_TEMPLATES.find((segment) => score >= segment.minScore && score <= segment.maxScore);
  return template?.segmentName ?? "Niedrige Kundenwertspanne";
}

function criterionColumnName(criterion: Pick<CustomerValueCriterion, "name">): string {
  return criterion.name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function generateCustomerValueCsvTemplate(criteria: CustomerValueCriterion[]): string {
  const activeCriteria = criteria.filter((criterion) => criterion.isActive);
  const dynamicColumns = activeCriteria.flatMap((criterion) => {
    const name = criterionColumnName(criterion);
    return [`${name}_score`, `${name}_weight`];
  });
  const columns = [
    "customer_name",
    "current_segment",
    "revenue_estimate",
    "margin_estimate",
    ...dynamicColumns,
    "customer_value_score",
    "customer_value_segment",
    "recommendation",
    "notes"
  ];

  return `${columns.join(",")}\n`;
}

export function generateCrmFieldMapping(): string {
  const rows = [
    ["crm_field_name", "field_type", "description", "example_value"],
    ["customer_value_score", "number", "Calculated Customer Value Score from 0 to 100", "82"],
    ["customer_value_segment", "single select", "Segment based on Customer Value Score", "Höchste Wertspanne"],
    ["customer_fit_score", "number", "Fit-related score based on strategic and value criteria", "78"],
    ["customer_effort_score", "number", "Effort-related score based on negative effort criteria", "35"],
    [
      "customer_value_recommendation",
      "text",
      "Suggested next action based on score and segment",
      "Priorisieren"
    ]
  ];

  return `${rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n")}\n`;
}
